import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as api from '../services/playbook-api.js';
import type {
  PlaybookBlock,
  PlaybookDefinition,
  PlaybookEvent,
  PlaybookRun,
  PlaybookRunStatus,
  RunWithEvents,
} from '../services/playbook-api.js';
import * as companyApi from '../services/company-api.js';
import { listGoalTree, type GoalNode } from '../services/goal-tree-api.js';
import { listGoogleAccounts, type GoogleAccount } from '../services/google-account-api.js';

interface CompanyLite { id: number; name: string }

interface FieldDef { key: string; label: string; kind: 'text' | 'textarea' | 'number' | 'select'; placeholder?: string; options?: Array<{ value: string; label: string }> }
interface BlockDef { type: string; label: string; icon: string; fields: FieldDef[]; branching?: boolean }

/**
 * Block-Katalog (Spiegel von backend `block-catalog.ts`): Reihenfolge = Palette-Reihenfolge.
 * `trigger` ist der Einstiegs-Baustein (kein Config, genau einmal automatisch vorhanden).
 */
const BLOCK_DEFS: BlockDef[] = [
  { type: 'trigger', label: 'Auslöser', icon: '◉', fields: [] },
  { type: 'approval_gate', label: 'Freigabe', icon: '✓', fields: [
    { key: 'summary', label: 'Hinweis für die Freigabe', kind: 'text', placeholder: 'Mail an {{ sender }} freigeben?' },
  ] },
  { type: 'send_email', label: 'E-Mail senden', icon: '✉', fields: [
    { key: 'to', label: 'An', kind: 'text', placeholder: '{{ sender }}' },
    { key: 'subject', label: 'Betreff', kind: 'text', placeholder: '' },
    { key: 'body', label: 'Text', kind: 'textarea', placeholder: 'Hallo {{ sender }}, …' },
    { key: 'cc', label: 'CC (optional)', kind: 'text', placeholder: '' },
    { key: 'accountAlias', label: 'Absender-Account', kind: 'select', options: [] },
  ] },
  { type: 'reply_email', label: 'Antworten (Thread)', icon: '↩', fields: [
    { key: 'to', label: 'An', kind: 'text', placeholder: '{{ sender }}' },
    { key: 'subject', label: 'Betreff', kind: 'text', placeholder: '{{ subject }}' },
    { key: 'body', label: 'Text', kind: 'textarea', placeholder: '{{ aiText }}' },
    { key: 'threadId', label: 'Thread-ID', kind: 'text', placeholder: '{{ threadId }}' },
    { key: 'messageId', label: 'Message-ID', kind: 'text', placeholder: '{{ messageId }}' },
  ] },
  { type: 'applai_create_project', label: 'Applai-Projekt anlegen', icon: '▦', fields: [
    { key: 'title', label: 'Titel', kind: 'text', placeholder: '{{ subject }}' },
    { key: 'description', label: 'Beschreibung', kind: 'textarea', placeholder: '{{ text }}' },
    { key: 'contactEmail', label: 'Kontakt-E-Mail (optional)', kind: 'text', placeholder: '{{ sender }}' },
  ] },
  { type: 'applai_fetch_matchings', label: 'Matchings abrufen', icon: '✷', fields: [
    { key: 'tenderId', label: 'Tender-ID', kind: 'text', placeholder: '{{ tenderId }}' },
  ] },
  { type: 'join_extract_cv', label: 'CV-Eckdaten extrahieren', icon: '📄', fields: [
    { key: 'cvUrlKey', label: 'CV-URL-Quelle', kind: 'text', placeholder: '{{ cvUrl }}' },
    { key: 'model', label: 'Modell', kind: 'select', options: [
      { value: '', label: 'Standard (Haiku 4.5)' },
      { value: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5' },
      { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6' },
      { value: 'claude-opus-4-8', label: 'Opus 4.8' },
    ] },
  ] },
  { type: 'lookup_person', label: 'Person-Lookup (Anrede)', icon: '👤', fields: [
    { key: 'emailKey', label: 'E-Mail-Quelle', kind: 'text', placeholder: '{{ sender }}' },
    { key: 'nameKey', label: 'Name-Quelle (optional)', kind: 'text', placeholder: '{{ entities.name }}' },
  ] },
  { type: 'ai_generate_text', label: 'KI-Text erzeugen', icon: '✨', fields: [
    { key: 'prompt', label: 'Prompt', kind: 'textarea', placeholder: 'Schreibe eine Antwort an {{ sender }} …' },
    { key: 'outputKey', label: 'Speichern als (Variable)', kind: 'text', placeholder: 'aiText' },
    { key: 'model', label: 'Modell', kind: 'select', options: [
      { value: '', label: 'Standard (Opus 4.8)' },
      { value: 'claude-opus-4-8', label: 'Opus 4.8' },
      { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6' },
      { value: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5' },
    ] },
  ] },
  { type: 'wait', label: 'Warten', icon: '⏱', fields: [
    { key: 'days', label: 'Tage', kind: 'number', placeholder: '0' },
    { key: 'hours', label: 'Stunden', kind: 'number', placeholder: '0' },
    { key: 'minutes', label: 'Minuten', kind: 'number', placeholder: '0' },
    { key: 'seconds', label: 'Sekunden', kind: 'number', placeholder: '0' },
    { key: 'until', label: 'oder bis (ISO-Zeitpunkt)', kind: 'text', placeholder: '2026-06-08T09:00:00Z' },
  ] },
  { type: 'condition', label: 'Bedingung / Verzweigung', icon: '⑂', branching: true, fields: [
    { key: 'expression', label: 'Ausdruck', kind: 'text', placeholder: 'matchings.length > 0' },
  ] },
];

const DEF_BY_TYPE = new Map(BLOCK_DEFS.map((d) => [d.type, d]));

/** Variablen-Picker (FR5): im Context verfügbare `{{ }}`-Tokens, gruppiert nach Herkunft. */
const VARIABLES: { group: string; vars: { token: string; hint: string }[] }[] = [
  { group: 'Auslöser-Signal', vars: [
    { token: 'sender', hint: 'Absender-Adresse' },
    { token: 'subject', hint: 'Betreff' },
    { token: 'text', hint: 'Volltext' },
    { token: 'threadId', hint: 'E-Mail-Thread' },
    { token: 'messageId', hint: 'E-Mail-Message' },
    { token: 'category', hint: 'LLM-Kategorie (z.B. Projektanfrage) — für Bedingungen' },
    { token: 'categoryReason', hint: 'Begründung der Kategorie' },
  ] },
  { group: 'Aus Bausteinen', vars: [
    { token: 'tenderId', hint: 'aus „Applai-Projekt anlegen"' },
    { token: 'tenderTitle', hint: 'aus „Applai-Projekt anlegen"' },
    { token: 'matchings', hint: 'aus „Matchings abrufen"' },
    { token: 'matchingsCount', hint: 'Anzahl Matchings' },
    { token: 'aiText', hint: 'aus „KI-Text erzeugen"' },
    { token: 'anrede', hint: 'aus „Person-Lookup" (z.B. Hallo Max / Sehr geehrte Frau …)' },
    { token: 'personName', hint: 'aus „Person-Lookup" — voller Name der Person' },
    { token: 'personKnown', hint: 'aus „Person-Lookup" — true/false' },
    { token: 'personMatch', hint: 'aus „Person-Lookup" — email | name | none' },
    { token: 'personNotes', hint: 'aus „Person-Lookup" — Notizen aus people_sections' },
  ] },
];

const DEFAULT_COMPANY_ID = 3;

/** Live-/History-Polling-Intervall (wie bestehende Live-Seiten, z.B. cp-page-monitoring). */
const LIVE_POLL_MS = 5_000;

/**
 * Beispiel-Kontext für Dry-Runs (FR20): füllt die `{{ }}`-Tokens des Auslöser-Signals, damit
 * eine Kette im Dry-Run möglichst weit läuft (reply_email braucht z.B. threadId/messageId).
 * Keine Außenwirkung — DryRunEffects simuliert alle Bausteine.
 */
const DRY_RUN_SAMPLE_CONTEXT: Record<string, unknown> = {
  sender: 'test@example.com',
  subject: 'Test-Anfrage (Dry-Run)',
  text: 'Beispieltext für den Dry-Run.',
  threadId: 'thread-dry-run',
  messageId: 'msg-dry-run',
  entities: { name: 'Max Mustermann' },
};

/** Status → Label + CSS-Klasse für die Run-Anzeige (FR19/FR21). */
const RUN_STATUS_INFO: Record<PlaybookRunStatus, { label: string; cls: string }> = {
  running: { label: 'läuft', cls: 'run' },
  awaiting_delay: { label: 'wartet', cls: 'wait' },
  awaiting_approval: { label: 'Freigabe offen', cls: 'wait' },
  done: { label: 'fertig', cls: 'ok' },
  failed: { label: 'fehlgeschlagen', cls: 'err' },
  stopped: { label: 'abgebrochen', cls: 'stop' },
};

/**
 * Playbook-Editor (compass-new, Migration 117 · Story 11, FR4/FR5/FR22/FR23). Michael baut pro
 * Ziel eine lineare Bausteinkette ohne Code: vertikale Block-Liste, Konfig-Felder pro Baustein,
 * Variablen-Picker (`{{ }}`) und Ja/Nein-Verzweigung beim Bedingungs-Baustein. Persistenz über
 * `/api/playbooks` (Backend Story 10). Run-Historie/Live-Status/Dry-Run-UI ergänzt Story 12.
 *
 * Editier-Modell: Die Block-Struktur (Reihenfolge, `next`/`nextTrue`/`nextFalse`) liegt im
 * `_editing`-Modell; die Text-Config-Felder sind uncontrolled (wie cp-page-signal-sources) und
 * werden vor jeder strukturellen Änderung und beim Speichern aus dem DOM zurückgelesen — so gibt
 * es keine Cursor-Sprünge beim Tippen.
 */
@customElement('cp-page-playbooks')
export class CpPagePlaybooks extends LitElement {
  createRenderRoot() { return this; }

  @state() private _companies: CompanyLite[] = [];
  @state() private _companyId = DEFAULT_COMPANY_ID;
  @state() private _goals: GoalNode[] = [];
  /** Verbundene Google-Accounts — Optionen für das Absender-Feld des „E-Mail senden"-Bausteins. */
  @state() private _accounts: GoogleAccount[] = [];
  @state() private _playbooks: PlaybookDefinition[] = [];
  @state() private _editing: PlaybookDefinition | null = null;
  @state() private _loading = false;
  @state() private _busy = false;
  @state() private _error = '';
  @state() private _notice = '';

  // --- Observability (Story 12, FR19/FR20/FR21) ---
  /** Aktive Top-Level-Ansicht: Editor (Definitionen) oder firmenweiter Live-Status. */
  @state() private _view: 'editor' | 'live' = 'editor';
  /** Unter-Tab des Editors: Bausteine bearbeiten oder Lauf-Historie + Dry-Run. */
  @state() private _editorTab: 'edit' | 'runs' = 'edit';
  @state() private _liveRuns: PlaybookRun[] = [];
  @state() private _runs: PlaybookRun[] = [];
  @state() private _runsLoading = false;
  @state() private _dryBusy = false;
  @state() private _dryResult: RunWithEvents | null = null;
  /** Geöffneter Run im Detail-Overlay (Event-Stream). */
  @state() private _selectedRun: RunWithEvents | null = null;

  /** Zuletzt fokussiertes Config-Feld (für den Variablen-Picker). */
  private _focused: { blockId: string; key: string } | null = null;
  private _liveTimer: ReturnType<typeof setInterval> | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadMeta();
    void this._load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopLivePolling();
  }

  private async _loadMeta(): Promise<void> {
    try { this._companies = (await companyApi.listCompanies()) as unknown as CompanyLite[]; } catch { /* ignore */ }
    try { this._goals = await listGoalTree(this._companyId); } catch { this._goals = []; }
    try { this._accounts = await listGoogleAccounts(); } catch { this._accounts = []; }
  }

  private async _load(): Promise<void> {
    this._loading = true; this._error = '';
    try { this._playbooks = await api.listPlaybooks(this._companyId); }
    catch (e) { this._error = this._msg(e); }
    finally { this._loading = false; }
  }

  private _msg(e: unknown): string { return e instanceof Error ? e.message : 'Fehler'; }

  private async _onCompanyChange(id: number): Promise<void> {
    this._companyId = id;
    this._editing = null;
    this._dryResult = null;
    try { this._goals = await listGoalTree(id); } catch { this._goals = []; }
    await this._load();
    if (this._view === 'live') await this._loadLive();
  }

  // --- Observability: Ansicht + Polling (Story 12) ----------------------------------------------

  private _stopLivePolling(): void {
    if (this._liveTimer) { clearInterval(this._liveTimer); this._liveTimer = null; }
  }

  private _startLivePolling(): void {
    this._stopLivePolling();
    this._liveTimer = setInterval(() => void this._loadLive(), LIVE_POLL_MS);
  }

  private async _setView(view: 'editor' | 'live'): Promise<void> {
    if (this._view === view) return;
    this._view = view;
    this._error = '';
    if (view === 'live') { await this._loadLive(); this._startLivePolling(); }
    else { this._stopLivePolling(); }
  }

  /** Firmenweite laufende/pausierte Runs (FR21). */
  private async _loadLive(): Promise<void> {
    try { this._liveRuns = await api.listLiveRuns(this._companyId); }
    catch (e) { this._error = this._msg(e); }
  }

  /** Lauf-Historie der gerade editierten Definition (FR19). */
  private async _loadRuns(): Promise<void> {
    if (!this._editing || this._editing.id === 0) { this._runs = []; return; }
    this._runsLoading = true;
    try { this._runs = await api.listRuns(this._editing.id, 50); }
    catch (e) { this._error = this._msg(e); }
    finally { this._runsLoading = false; }
  }

  private async _setEditorTab(tab: 'edit' | 'runs'): Promise<void> {
    this._editorTab = tab;
    if (tab === 'runs') await this._loadRuns();
  }

  /** Dry-Run der editierten Definition starten (FR20). Keine Außenwirkung. */
  private async _dryRun(): Promise<void> {
    if (!this._editing || this._editing.id === 0) return;
    this._dryBusy = true; this._error = ''; this._dryResult = null;
    try { this._dryResult = await api.dryRunPlaybook(this._editing.id, DRY_RUN_SAMPLE_CONTEXT); }
    catch (e) { this._error = this._msg(e); }
    finally { this._dryBusy = false; }
  }

  private async _openRun(runId: string): Promise<void> {
    this._selectedRun = null;
    try { this._selectedRun = await api.getRun(runId); }
    catch (e) { this._error = this._msg(e); }
  }

  private _closeRun(): void { this._selectedRun = null; }

  private _goalName(id: number): string {
    const g = this._goals.find((x) => x.id === id);
    return g ? `${g.text} (${g.type})` : `Ziel #${id}`;
  }

  // --- Editor öffnen / anlegen -----------------------------------------------------------------

  private _clone(def: PlaybookDefinition): PlaybookDefinition {
    return JSON.parse(JSON.stringify(def)) as PlaybookDefinition;
  }

  private _edit(def: PlaybookDefinition): void {
    this._editing = this._clone(def);
    this._editorTab = 'edit';
    this._dryResult = null;
    this._view = 'editor';
    this._stopLivePolling();
    this._error = ''; this._notice = '';
  }

  private _newPlaybook(): void {
    const firstGoal = this._goals.find((g) => g.type === 'pillar') ?? this._goals[0];
    this._editing = {
      id: 0,
      companyId: this._companyId,
      goalNodeId: firstGoal?.id ?? 0,
      name: 'Neues Playbook',
      enabled: false,
      blocks: [{ id: this._blockId('trigger'), type: 'trigger', next: null, config: {} }],
      createdAt: '',
      updatedAt: '',
    };
    this._editorTab = 'edit';
    this._dryResult = null;
    this._view = 'editor';
    this._stopLivePolling();
    this._error = ''; this._notice = '';
  }

  private _cancel(): void { this._editing = null; }

  private _blockId(type: string): string {
    const rnd = (crypto.randomUUID?.() ?? String(Math.random())).slice(0, 8);
    return `${type}-${rnd}`;
  }

  // --- DOM <-> Modell ---------------------------------------------------------------------------

  private _fieldEl(blockId: string, key: string): HTMLInputElement | HTMLTextAreaElement | null {
    return this.querySelector(`#f-${blockId}-${key}`) as HTMLInputElement | HTMLTextAreaElement | null;
  }

  /** Liest alle Config-Felder + Name aus dem DOM zurück ins `_editing`-Modell. */
  private _syncFromDom(): void {
    if (!this._editing) return;
    const nameEl = this.querySelector('#pb-name') as HTMLInputElement | null;
    if (nameEl) this._editing.name = nameEl.value;
    for (const block of this._editing.blocks) {
      const def = DEF_BY_TYPE.get(block.type);
      if (!def) continue;
      const cfg: Record<string, unknown> = {};
      for (const f of def.fields) {
        const el = this._fieldEl(block.id, f.key);
        if (!el) { if (block.config?.[f.key] !== undefined) cfg[f.key] = block.config[f.key]; continue; }
        const raw = el.value.trim();
        if (raw === '') continue;
        if (f.kind === 'number') {
          const n = Number(raw);
          if (!Number.isNaN(n)) cfg[f.key] = n; // ungültige Zahl ignorieren statt NaN zu serialisieren
        } else {
          cfg[f.key] = el.value;
        }
      }
      block.config = cfg;
    }
  }

  /** Strukturelle Mutation: erst DOM sichern, dann `mutate`, dann re-render. */
  private _mutate(mutate: () => void): void {
    if (!this._editing) return;
    this._syncFromDom();
    mutate();
    this._editing = { ...this._editing, blocks: [...this._editing.blocks] };
  }

  // --- Block-Operationen ------------------------------------------------------------------------

  private _addBlock(type: string): void {
    this._mutate(() => {
      const def = DEF_BY_TYPE.get(type)!;
      const id = this._blockId(type);
      const block: PlaybookBlock = def.branching
        ? { id, type, nextTrue: null, nextFalse: null, config: {} }
        : { id, type, next: null, config: {} };
      const blocks = this._editing!.blocks;
      // Auto-Verkettung: hängt den neuen Baustein ans Ende der linearen Kette an.
      const last = blocks[blocks.length - 1];
      if (last && !DEF_BY_TYPE.get(last.type)?.branching && (last.next == null)) {
        last.next = id;
      }
      blocks.push(block);
    });
  }

  private _removeBlock(blockId: string): void {
    this._mutate(() => {
      const blocks = this._editing!.blocks;
      const idx = blocks.findIndex((b) => b.id === blockId);
      if (idx < 0) return;
      const removed = blocks[idx];
      blocks.splice(idx, 1);
      // Verweise auf den entfernten Baustein neutralisieren (auf dessen `next` umbiegen).
      const fallback = removed.next ?? null;
      for (const b of blocks) {
        if (b.next === blockId) b.next = fallback;
        if (b.nextTrue === blockId) b.nextTrue = null;
        if (b.nextFalse === blockId) b.nextFalse = null;
      }
    });
  }

  private _moveBlock(blockId: string, dir: -1 | 1): void {
    this._mutate(() => {
      const blocks = this._editing!.blocks;
      const idx = blocks.findIndex((b) => b.id === blockId);
      const to = idx + dir;
      if (idx < 0 || to < 0 || to >= blocks.length) return;
      [blocks[idx], blocks[to]] = [blocks[to], blocks[idx]];
    });
  }

  private _setNext(block: PlaybookBlock, field: 'next' | 'nextTrue' | 'nextFalse', value: string): void {
    this._mutate(() => { (block as unknown as Record<string, unknown>)[field] = value === '' ? null : value; });
  }

  // --- Variablen-Picker -------------------------------------------------------------------------

  private _insertVar(token: string): void {
    if (!this._focused) { this._notice = `Erst ein Textfeld anklicken, dann „{{ ${token} }}" einfügen.`; return; }
    const el = this._fieldEl(this._focused.blockId, this._focused.key);
    if (!el) return;
    const snippet = `{{ ${token} }}`;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    el.value = el.value.slice(0, start) + snippet + el.value.slice(end);
    const caret = start + snippet.length;
    el.focus();
    el.setSelectionRange(caret, caret);
  }

  // --- Speichern / Löschen ----------------------------------------------------------------------

  private async _save(): Promise<void> {
    if (!this._editing) return;
    this._syncFromDom();
    const ed = this._editing;
    if (!ed.name.trim()) { this._error = 'Name fehlt'; return; }
    if (!ed.goalNodeId) { this._error = 'Ziel fehlt'; return; }
    this._busy = true; this._error = ''; this._notice = '';
    try {
      if (ed.id === 0) {
        const created = await api.createPlaybook({
          companyId: ed.companyId, goalNodeId: ed.goalNodeId, name: ed.name.trim(), enabled: ed.enabled, blocks: ed.blocks,
        });
        this._notice = 'Playbook angelegt.';
        this._editing = this._clone(created);
      } else {
        const updated = await api.updatePlaybook(ed.id, { name: ed.name.trim(), enabled: ed.enabled, blocks: ed.blocks });
        this._notice = 'Gespeichert.';
        this._editing = this._clone(updated);
      }
      await this._load();
    } catch (e) { this._error = this._msg(e); }
    finally { this._busy = false; }
  }

  private async _delete(): Promise<void> {
    if (!this._editing || this._editing.id === 0) { this._editing = null; return; }
    if (!confirm(`Playbook „${this._editing.name}" löschen?`)) return;
    this._busy = true; this._error = '';
    try {
      await api.deletePlaybook(this._editing.id);
      this._editing = null;
      await this._load();
    } catch (e) { this._error = this._msg(e); }
    finally { this._busy = false; }
  }

  // --- Render -----------------------------------------------------------------------------------

  private _nextOptions(current: PlaybookBlock, selected: string | null | undefined) {
    const others = this._editing!.blocks.filter((b) => b.id !== current.id);
    return html`
      <option value="" ?selected=${selected == null}>— Ende —</option>
      ${others.map((b) => html`<option value=${b.id} ?selected=${selected === b.id}>${this._blockTitle(b)}</option>`)}
    `;
  }

  private _blockTitle(b: PlaybookBlock): string {
    return DEF_BY_TYPE.get(b.type)?.label ?? b.type;
  }

  private _renderField(block: PlaybookBlock, f: FieldDef) {
    const val = String(block.config?.[f.key] ?? '');
    const onFocus = () => { this._focused = { blockId: block.id, key: f.key }; };
    if (f.kind === 'textarea') {
      return html`<label class="pb-f">${f.label}
        <textarea id="f-${block.id}-${f.key}" rows="3" placeholder=${f.placeholder ?? ''} .value=${val} @focus=${onFocus}></textarea>
      </label>`;
    }
    if (f.kind === 'select') {
      // Absender-Account: Optionen dynamisch aus den verbundenen Google-Accounts. Leerwert =
      // Effects-Default (Origin-Account aus dem Kontext bzw. konfigurierter Standard).
      const options = f.key === 'accountAlias'
        ? [{ value: '', label: 'Standard (Absender aus Kontext)' },
           ...this._accounts.map((a) => ({ value: a.account_alias, label: `${a.account_alias} — ${a.google_email}` }))]
        : (f.options ?? []);
      return html`<label class="pb-f">${f.label}
        <select id="f-${block.id}-${f.key}" .value=${val} @focus=${onFocus}>
          ${options.map((o) => html`<option value=${o.value} ?selected=${val === o.value}>${o.label}</option>`)}
        </select>
      </label>`;
    }
    return html`<label class="pb-f">${f.label}
      <input id="f-${block.id}-${f.key}" type=${f.kind === 'number' ? 'number' : 'text'} placeholder=${f.placeholder ?? ''} .value=${val} @focus=${onFocus} />
    </label>`;
  }

  private _renderBlock(block: PlaybookBlock, idx: number) {
    const def = DEF_BY_TYPE.get(block.type);
    const isTrigger = block.type === 'trigger';
    return html`
      <div class="pb-block ${def?.branching ? 'pb-block--branch' : ''}">
        <div class="pb-block-head">
          <span class="pb-block-icon">${def?.icon ?? '■'}</span>
          <span class="pb-block-title">${idx + 1}. ${def?.label ?? block.type}</span>
          ${!isTrigger ? html`
            <span class="pb-block-tools">
              <button title="Hoch" ?disabled=${idx <= 1} @click=${() => this._moveBlock(block.id, -1)}>↑</button>
              <button title="Runter" ?disabled=${idx >= this._editing!.blocks.length - 1} @click=${() => this._moveBlock(block.id, 1)}>↓</button>
              <button class="pb-del" title="Entfernen" @click=${() => this._removeBlock(block.id)}>✕</button>
            </span>` : html`<span class="pb-block-tools"><em class="pb-trigger-hint">Einstieg</em></span>`}
        </div>
        ${def && def.fields.length ? html`<div class="pb-fields">${def.fields.map((f) => this._renderField(block, f))}</div>` : nothing}
        <div class="pb-routing">
          ${def?.branching ? html`
            <label class="pb-f">Wenn wahr → <select @change=${(e: Event) => this._setNext(block, 'nextTrue', (e.target as HTMLSelectElement).value)}>${this._nextOptions(block, block.nextTrue)}</select></label>
            <label class="pb-f">Wenn falsch → <select @change=${(e: Event) => this._setNext(block, 'nextFalse', (e.target as HTMLSelectElement).value)}>${this._nextOptions(block, block.nextFalse)}</select></label>
          ` : html`
            <label class="pb-f">Weiter zu <select @change=${(e: Event) => this._setNext(block, 'next', (e.target as HTMLSelectElement).value)}>${this._nextOptions(block, block.next)}</select></label>
          `}
        </div>
      </div>
    `;
  }

  private _renderEditor() {
    const ed = this._editing!;
    return html`
      <div class="pb-editor">
        <div class="pb-editor-head">
          <input id="pb-name" class="pb-name" .value=${ed.name} placeholder="Name des Playbooks" />
          <label class="pb-enabled">
            <input type="checkbox" ?checked=${ed.enabled} @change=${(e: Event) => { ed.enabled = (e.target as HTMLInputElement).checked; this._editing = { ...ed }; }} />
            Aktiv
          </label>
        </div>
        <label class="pb-f pb-goal">Gebunden an Ziel
          <select @change=${(e: Event) => { ed.goalNodeId = Number((e.target as HTMLSelectElement).value); this._editing = { ...ed }; }}>
            ${this._goals.length === 0 ? html`<option value=${ed.goalNodeId}>Ziel #${ed.goalNodeId}</option>` : nothing}
            ${this._goals.map((g) => html`<option value=${g.id} ?selected=${g.id === ed.goalNodeId}>${g.text} · ${g.type}</option>`)}
          </select>
        </label>
        <p class="pb-hint">Genau ein <strong>aktives</strong> Playbook pro Ziel. Bei Speichern eines zweiten aktiven Playbooks für dasselbe Ziel meldet das Backend einen Konflikt (409).</p>

        <div class="pb-block-list">
          ${ed.blocks.map((b, i) => this._renderBlock(b, i))}
        </div>

        <div class="pb-palette">
          <span class="pb-palette-label">Baustein hinzufügen:</span>
          ${BLOCK_DEFS.filter((d) => d.type !== 'trigger').map((d) => html`
            <button class="pb-add" @click=${() => this._addBlock(d.type)}>${d.icon} ${d.label}</button>
          `)}
        </div>

        <div class="pb-actions">
          <button class="pb-save" ?disabled=${this._busy} @click=${() => void this._save()}>${this._busy ? 'Speichert…' : (ed.id === 0 ? 'Anlegen' : 'Speichern')}</button>
          <button class="pb-cancel" ?disabled=${this._busy} @click=${() => this._cancel()}>Schließen</button>
          ${ed.id !== 0 ? html`<button class="pb-delete" ?disabled=${this._busy} @click=${() => void this._delete()}>Löschen</button>` : nothing}
        </div>
      </div>

      <aside class="pb-vars">
        <h3>Variablen</h3>
        <p class="pb-vars-hint">In ein Textfeld klicken, dann einfügen. Tokens werden zur Laufzeit ersetzt.</p>
        ${VARIABLES.map((grp) => html`
          <div class="pb-vars-group">
            <span class="pb-vars-grouplabel">${grp.group}</span>
            ${grp.vars.map((v) => html`<button class="pb-var" title=${v.hint} @click=${() => this._insertVar(v.token)}>{{ ${v.token} }}</button>`)}
          </div>
        `)}
      </aside>
    `;
  }

  // --- Render: Observability (Story 12) ---------------------------------------------------------

  private _playbookName(id: number): string {
    return this._playbooks.find((p) => p.id === id)?.name ?? `Playbook #${id}`;
  }

  private _time(iso: unknown): string {
    if (typeof iso !== 'string' || !iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' });
  }

  private _statusBadge(s: PlaybookRunStatus) {
    const i = RUN_STATUS_INFO[s] ?? { label: s, cls: '' };
    return html`<span class="pb-badge pb-badge--${i.cls}">${i.label}</span>`;
  }

  private _renderEvents(events: PlaybookEvent[]) {
    if (!events.length) return html`<p class="pb-empty">Noch keine Events.</p>`;
    return html`
      <ol class="pb-events">
        ${events.map((ev) => {
          const p = (ev.payload ?? {}) as Record<string, unknown>;
          const block = p.type ?? p.block_id;
          return html`<li class="pb-event">
            <span class="pb-event-type">${ev.eventType.replace(/^playbook\./, '')}</span>
            ${block ? html`<span class="pb-event-block">${String(block)}</span>` : nothing}
            ${p.simulated ? html`<span class="pb-tag">simuliert</span>` : nothing}
            ${p.reason ? html`<span class="pb-tag">${String(p.reason)}</span>` : nothing}
            ${p.error ? html`<span class="pb-run-err">${String(p.error)}</span>` : nothing}
            <span class="pb-event-time">${this._time(ev.occurredAt ?? ev.createdAt)}</span>
          </li>`;
        })}
      </ol>
    `;
  }

  private _renderRunRow(r: PlaybookRun, showPlaybook: boolean) {
    return html`
      <div class="pb-run" @click=${() => void this._openRun(r.id)}>
        <div class="pb-run-main">
          ${showPlaybook ? html`<span class="pb-run-pb">${this._playbookName(r.playbookId)}</span>` : nothing}
          ${this._statusBadge(r.status)}
          ${r.dryRun ? html`<span class="pb-tag">Dry-Run</span>` : nothing}
          ${r.currentBlockId ? html`<span class="pb-run-block">@ ${r.currentBlockId}</span>` : nothing}
        </div>
        <div class="pb-run-meta">
          <span>gestartet ${this._time(r.createdAt)}</span>
          <span>· aktualisiert ${this._time(r.updatedAt)}</span>
        </div>
        ${r.error ? html`<div class="pb-run-err">${r.error}</div>` : nothing}
      </div>
    `;
  }

  private _renderRunsTab() {
    return html`
      <div class="pb-runs">
        <div class="pb-runs-bar">
          <button class="pb-dry" ?disabled=${this._dryBusy} @click=${() => void this._dryRun()}>
            ${this._dryBusy ? 'Dry-Run läuft…' : '▷ Dry-Run starten'}
          </button>
          <button class="pb-refresh" title="Aktualisieren" @click=${() => void this._loadRuns()}>↻</button>
          <span class="pb-runs-hint">Dry-Run testet die Kette ohne Außenwirkung (Beispiel-Kontext).</span>
        </div>
        ${this._dryResult ? html`
          <div class="pb-dry-result">
            <div class="pb-dry-head">
              Dry-Run-Ergebnis ${this._statusBadge(this._dryResult.run.status)}
              ${this._dryResult.run.error ? html`<span class="pb-run-err">${this._dryResult.run.error}</span>` : nothing}
            </div>
            ${this._renderEvents(this._dryResult.events)}
          </div>` : nothing}
        <h4 class="pb-runs-title">Lauf-Historie</h4>
        ${this._runsLoading ? html`<p>Lädt…</p>` : nothing}
        ${!this._runsLoading && this._runs.length === 0 ? html`<p class="pb-empty">Noch keine Läufe für dieses Playbook.</p>` : nothing}
        <div class="pb-run-list">
          ${this._runs.map((r) => this._renderRunRow(r, false))}
        </div>
      </div>
    `;
  }

  private _renderLive() {
    return html`
      <div class="pb-live">
        <div class="pb-runs-bar">
          <button class="pb-refresh" title="Aktualisieren" @click=${() => void this._loadLive()}>↻</button>
          <span class="pb-runs-hint">Laufende und pausierte Runs dieser Firma. Aktualisiert automatisch alle ${LIVE_POLL_MS / 1000}s.</span>
        </div>
        ${this._liveRuns.length === 0 ? html`<p class="pb-empty">Aktuell keine laufenden Runs.</p>` : nothing}
        <div class="pb-run-list">
          ${this._liveRuns.map((r) => this._renderRunRow(r, true))}
        </div>
      </div>
    `;
  }

  private _renderRunOverlay() {
    if (!this._selectedRun) return nothing;
    const { run, events } = this._selectedRun;
    return html`
      <div class="pb-overlay" @click=${() => this._closeRun()}>
        <div class="pb-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="pb-modal-head">
            <span class="pb-modal-title">${this._playbookName(run.playbookId)} ${this._statusBadge(run.status)} ${run.dryRun ? html`<span class="pb-tag">Dry-Run</span>` : nothing}</span>
            <button class="pb-modal-close" @click=${() => this._closeRun()}>✕</button>
          </div>
          <div class="pb-modal-meta">
            <span>Run ${run.id}</span>
            <span>gestartet ${this._time(run.createdAt)}</span>
            <span>aktualisiert ${this._time(run.updatedAt)}</span>
            ${run.currentBlockId ? html`<span>aktueller Baustein: ${run.currentBlockId}</span>` : nothing}
          </div>
          ${run.error ? html`<p class="pb-run-err">${run.error}</p>` : nothing}
          ${this._renderEvents(events)}
        </div>
      </div>
    `;
  }

  private _renderDetail() {
    const ed = this._editing!;
    const hasRuns = ed.id !== 0;
    return html`
      <div class="pb-detail-wrap">
        ${hasRuns ? html`
          <div class="pb-subtabs">
            <button class="pb-subtab ${this._editorTab === 'edit' ? 'active' : ''}" @click=${() => void this._setEditorTab('edit')}>Bearbeiten</button>
            <button class="pb-subtab ${this._editorTab === 'runs' ? 'active' : ''}" @click=${() => void this._setEditorTab('runs')}>Läufe &amp; Dry-Run</button>
          </div>` : nothing}
        ${this._editorTab === 'runs' && hasRuns
          ? this._renderRunsTab()
          : html`<div class="pb-detail">${this._renderEditor()}</div>`}
      </div>
    `;
  }

  private _renderList() {
    return html`
      <div class="pb-list">
        ${this._loading ? html`<p>Lädt…</p>` : nothing}
        ${!this._loading && this._playbooks.length === 0 ? html`<p class="pb-empty">Noch kein Playbook für diese Firma. Rechts ein neues anlegen.</p>` : nothing}
        ${this._playbooks.map((p) => html`
          <div class="pb-card ${this._editing?.id === p.id ? 'pb-card--active' : ''}" @click=${() => this._edit(p)}>
            <div class="pb-card-row">
              <span class="pb-dot ${p.enabled ? 'on' : 'off'}" title=${p.enabled ? 'aktiv' : 'inaktiv'}></span>
              <span class="pb-card-name">${p.name}</span>
              <span class="pb-card-count">${p.blocks.length} Bausteine</span>
            </div>
            <div class="pb-card-goal">→ ${this._goalName(p.goalNodeId)}</div>
          </div>
        `)}
      </div>
    `;
  }

  override render() {
    return html`
      <style>
        .pb-wrap { padding: 1.5rem; max-width: 1180px; margin: 0 auto; font-family: var(--font-family-sans); color: var(--color-text-primary); }
        .pb-head { display: flex; align-items: baseline; gap: 1rem; margin-bottom: .4rem; flex-wrap: wrap; }
        .pb-head h1 { font-size: 1.4rem; margin: 0; }
        .pb-head select { margin-left: .4rem; padding: .3rem .5rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; }
        .pb-new { margin-left: auto; cursor: pointer; border: none; background: var(--color-accent-primary, #1a7fd4); color: #fff; border-radius: 6px; padding: .45rem 1rem; font-weight: 600; }
        .pb-refresh { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .35rem .7rem; }
        .pb-intro { color: var(--color-text-secondary); font-size: .88rem; line-height: 1.5; margin: .3rem 0 1rem; }
        .pb-layout { display: grid; grid-template-columns: 300px 1fr; gap: 1.2rem; align-items: start; }
        .pb-list { display: flex; flex-direction: column; gap: .5rem; }
        .pb-empty { color: var(--color-text-muted); font-size: .85rem; }
        .pb-card { border: 1px solid var(--color-border); border-radius: 10px; padding: .7rem .8rem; background: var(--color-bg-secondary); cursor: pointer; }
        .pb-card:hover { border-color: var(--color-accent-primary, #1a7fd4); }
        .pb-card--active { border-color: var(--color-accent-primary, #1a7fd4); box-shadow: 0 0 0 1px var(--color-accent-primary, #1a7fd4) inset; }
        .pb-card-row { display: flex; align-items: center; gap: .5rem; }
        .pb-card-name { font-weight: 600; flex: 1; }
        .pb-card-count { font-size: .72rem; color: var(--color-text-muted); }
        .pb-card-goal { font-size: .76rem; color: var(--color-text-secondary); margin-top: .3rem; }
        .pb-dot { width: 9px; height: 9px; border-radius: 999px; display: inline-block; }
        .pb-dot.on { background: var(--color-accent-success, #2e7d32); } .pb-dot.off { background: #888; }
        .pb-detail { display: grid; grid-template-columns: 1fr 220px; gap: 1rem; align-items: start; }
        .pb-placeholder { color: var(--color-text-muted); border: 1px dashed var(--color-border); border-radius: 10px; padding: 2rem; text-align: center; }
        .pb-editor-head { display: flex; align-items: center; gap: 1rem; margin-bottom: .6rem; }
        .pb-name { flex: 1; font-size: 1.05rem; font-weight: 600; padding: .45rem .6rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; }
        .pb-enabled { font-size: .82rem; display: flex; align-items: center; gap: .35rem; color: var(--color-text-secondary); }
        .pb-goal { margin-bottom: .4rem; }
        .pb-hint { font-size: .76rem; color: var(--color-text-muted); margin: 0 0 1rem; }
        .pb-f { font-size: .74rem; color: var(--color-text-secondary); display: flex; flex-direction: column; gap: .22rem; }
        .pb-f input, .pb-f select, .pb-f textarea { font-size: .86rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; padding: .4rem .5rem; font-family: inherit; }
        .pb-f textarea { font-family: var(--font-family-mono); font-size: .82rem; resize: vertical; }
        .pb-block-list { display: flex; flex-direction: column; gap: .5rem; }
        .pb-block { border: 1px solid var(--color-border); border-radius: 10px; padding: .7rem .85rem; background: var(--color-bg-secondary); position: relative; }
        .pb-block--branch { border-color: #c8771a; }
        .pb-block::after { content: '↓'; position: absolute; left: 50%; bottom: -.65rem; transform: translateX(-50%); color: var(--color-text-muted); font-size: .8rem; }
        .pb-block:last-of-type::after { content: ''; }
        .pb-block-head { display: flex; align-items: center; gap: .5rem; margin-bottom: .5rem; }
        .pb-block-icon { font-size: 1rem; }
        .pb-block-title { font-weight: 600; font-size: .9rem; flex: 1; }
        .pb-block-tools { display: flex; gap: .25rem; align-items: center; }
        .pb-block-tools button { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 5px; padding: .15rem .4rem; font-size: .78rem; }
        .pb-block-tools button:disabled { opacity: .35; cursor: default; }
        .pb-del { color: var(--color-accent-error, #c33) !important; border-color: var(--color-accent-error, #c33) !important; }
        .pb-trigger-hint { font-size: .72rem; color: var(--color-text-muted); }
        .pb-fields { display: flex; flex-direction: column; gap: .45rem; }
        .pb-routing { display: flex; gap: .6rem; margin-top: .55rem; flex-wrap: wrap; }
        .pb-routing .pb-f { flex: 1; min-width: 150px; }
        .pb-palette { display: flex; flex-wrap: wrap; gap: .4rem; align-items: center; margin: 1rem 0; padding: .8rem; border: 1px dashed var(--color-border); border-radius: 10px; }
        .pb-palette-label { font-size: .78rem; color: var(--color-text-secondary); margin-right: .3rem; }
        .pb-add { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .35rem .6rem; font-size: .8rem; }
        .pb-add:hover { border-color: var(--color-accent-primary, #1a7fd4); }
        .pb-actions { display: flex; gap: .5rem; margin-top: .4rem; }
        .pb-save { cursor: pointer; border: none; background: var(--color-accent-primary, #1a7fd4); color: #fff; border-radius: 6px; padding: .5rem 1.2rem; font-weight: 600; }
        .pb-cancel { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .5rem 1rem; }
        .pb-delete { cursor: pointer; border: 1px solid var(--color-accent-error, #c33); background: transparent; color: var(--color-accent-error, #c33); border-radius: 6px; padding: .5rem 1rem; margin-left: auto; }
        .pb-vars { border: 1px solid var(--color-border); border-radius: 10px; padding: .8rem; background: var(--color-bg-secondary); position: sticky; top: 1rem; }
        .pb-vars h3 { margin: 0 0 .3rem; font-size: .95rem; }
        .pb-vars-hint { font-size: .72rem; color: var(--color-text-muted); margin: 0 0 .7rem; line-height: 1.4; }
        .pb-vars-group { margin-bottom: .7rem; }
        .pb-vars-grouplabel { display: block; font-size: .68rem; text-transform: uppercase; letter-spacing: .05em; color: var(--color-text-secondary); margin-bottom: .3rem; }
        .pb-var { display: inline-block; cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .2rem .45rem; font-size: .76rem; font-family: var(--font-family-mono); margin: 0 .25rem .25rem 0; }
        .pb-var:hover { border-color: var(--color-accent-primary, #1a7fd4); }
        .pb-msg-err { color: var(--color-accent-error, #c33); font-size: .85rem; }
        .pb-msg-ok { color: var(--color-accent-success, #2e7d32); font-size: .85rem; }
        /* --- Observability (Story 12): Tabs, Runs, Live, Overlay --- */
        .pb-tabs { display: flex; gap: .25rem; border-bottom: 1px solid var(--color-border); margin: .2rem 0 1rem; }
        .pb-tab { cursor: pointer; border: none; background: none; color: var(--color-text-secondary); padding: .5rem .9rem; font-size: .9rem; border-bottom: 2px solid transparent; margin-bottom: -1px; }
        .pb-tab.active { color: var(--color-text-primary); border-bottom-color: var(--color-accent-primary, #1a7fd4); font-weight: 600; }
        .pb-subtabs { display: flex; gap: .25rem; margin-bottom: .8rem; }
        .pb-subtab { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-secondary); padding: .35rem .8rem; font-size: .82rem; border-radius: 6px; }
        .pb-subtab.active { color: #fff; background: var(--color-accent-primary, #1a7fd4); border-color: var(--color-accent-primary, #1a7fd4); font-weight: 600; }
        .pb-runs-bar { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; margin-bottom: .8rem; }
        .pb-runs-hint { font-size: .76rem; color: var(--color-text-muted); }
        .pb-runs-title { font-size: .9rem; margin: 1rem 0 .5rem; }
        .pb-dry { cursor: pointer; border: none; background: var(--color-accent-primary, #1a7fd4); color: #fff; border-radius: 6px; padding: .45rem 1rem; font-weight: 600; }
        .pb-dry:disabled { opacity: .5; cursor: default; }
        .pb-run-list { display: flex; flex-direction: column; gap: .45rem; }
        .pb-run { border: 1px solid var(--color-border); border-radius: 8px; padding: .55rem .7rem; background: var(--color-bg-secondary); cursor: pointer; }
        .pb-run:hover { border-color: var(--color-accent-primary, #1a7fd4); }
        .pb-run-main { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; }
        .pb-run-pb { font-weight: 600; }
        .pb-run-block { font-size: .74rem; color: var(--color-text-muted); font-family: var(--font-family-mono); }
        .pb-run-meta { font-size: .72rem; color: var(--color-text-secondary); margin-top: .3rem; display: flex; gap: .4rem; flex-wrap: wrap; }
        .pb-run-err { color: var(--color-accent-error, #c33); font-size: .78rem; margin-top: .25rem; }
        .pb-badge { font-size: .7rem; padding: .12rem .45rem; border-radius: 999px; font-weight: 600; border: 1px solid transparent; }
        .pb-badge--run { background: rgba(26,127,212,.15); color: #1a7fd4; }
        .pb-badge--wait { background: rgba(200,119,26,.15); color: #c8771a; }
        .pb-badge--ok { background: rgba(46,125,50,.15); color: #2e7d32; }
        .pb-badge--err { background: rgba(204,51,51,.15); color: #c33; }
        .pb-badge--stop { background: rgba(120,120,120,.18); color: #888; }
        .pb-tag { font-size: .68rem; padding: .1rem .4rem; border-radius: 4px; background: var(--color-bg-tertiary); color: var(--color-text-secondary); border: 1px solid var(--color-border); }
        .pb-dry-result { border: 1px solid var(--color-border); border-radius: 8px; padding: .7rem; margin-bottom: 1rem; background: var(--color-bg-secondary); }
        .pb-dry-head { display: flex; align-items: center; gap: .5rem; font-weight: 600; font-size: .85rem; margin-bottom: .5rem; }
        .pb-events { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: .2rem; }
        .pb-event { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; font-size: .76rem; padding: .25rem .35rem; border-left: 2px solid var(--color-border); }
        .pb-event-type { font-family: var(--font-family-mono); color: var(--color-text-primary); }
        .pb-event-block { color: var(--color-text-secondary); }
        .pb-event-time { margin-left: auto; color: var(--color-text-muted); font-size: .7rem; }
        .pb-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem; }
        .pb-modal { background: var(--color-bg-primary, #1c1c1e); border: 1px solid var(--color-border); border-radius: 12px; padding: 1.2rem; max-width: 640px; width: 100%; max-height: 80vh; overflow: auto; }
        .pb-modal-head { display: flex; align-items: center; gap: .5rem; margin-bottom: .6rem; }
        .pb-modal-title { font-weight: 600; display: flex; align-items: center; gap: .5rem; flex: 1; }
        .pb-modal-close { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .2rem .5rem; }
        .pb-modal-meta { font-size: .74rem; color: var(--color-text-secondary); display: flex; gap: .6rem; flex-wrap: wrap; margin-bottom: .6rem; }
        @media (max-width: 880px) { .pb-layout { grid-template-columns: 1fr; } .pb-detail { grid-template-columns: 1fr; } }
      </style>
      <div class="pb-wrap">
        <div class="pb-head">
          <h1>Playbooks</h1>
          ${this._companies.length ? html`
            <label style="font-size:.8rem;color:var(--color-text-secondary)">Firma
              <select @change=${(e: Event) => void this._onCompanyChange(Number((e.target as HTMLSelectElement).value))}>
                ${this._companies.map((c) => html`<option value=${c.id} ?selected=${c.id === this._companyId}>${c.name} (#${c.id})</option>`)}
              </select>
            </label>` : nothing}
          <button class="pb-refresh" @click=${() => void this._load()}>↻</button>
          <button class="pb-new" @click=${() => this._newPlaybook()}>+ Neues Playbook</button>
        </div>
        <p class="pb-intro">
          Ein Playbook ist eine pro Ziel konfigurierbare Aktionskette: Nach einer Signal-Decision
          <em>Relevant</em> für das Ziel läuft die Kette automatisch (Applai, Mail, Antwort, Warten,
          Matchings, Bedingung, Freigabe, KI-Text). Bausteine vertikal anordnen, Felder mit
          <code>{{ Variablen }}</code> füllen, beim Bedingungs-Baustein verzweigen.
        </p>

        <div class="pb-tabs">
          <button class="pb-tab ${this._view === 'editor' ? 'active' : ''}" @click=${() => void this._setView('editor')}>Editor</button>
          <button class="pb-tab ${this._view === 'live' ? 'active' : ''}" @click=${() => void this._setView('live')}>Live-Runs</button>
        </div>

        ${this._error ? html`<p class="pb-msg-err">${this._error}</p>` : nothing}
        ${this._notice ? html`<p class="pb-msg-ok">${this._notice}</p>` : nothing}

        ${this._view === 'live'
          ? this._renderLive()
          : html`
            <div class="pb-layout">
              ${this._renderList()}
              ${this._editing
                ? this._renderDetail()
                : html`<div class="pb-placeholder">Links ein Playbook wählen oder oben rechts ein neues anlegen.</div>`}
            </div>`}
      </div>
      ${this._renderRunOverlay()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-playbooks': CpPagePlaybooks;
  }
}
